import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Shipping as ShippingIcon } from "@faire/web--source/slate/icons/foundation/Shipping";
import React from "react";

import { OTHER_NODES_LABEL } from "../../shared/other-node";
import { SelfServeNodes } from "../../types";
import { ChangeShippingAddressIA } from "../instantAnswers/ordersAndShipping-changeShippingAddress";
import { CreateReturnIA } from "../instantAnswers/ordersAndShipping-createReturn";
import { DutiesInvoiceIA } from "../instantAnswers/ordersAndShipping-dutiesInvoice";
import { EoriDetailsIA } from "../instantAnswers/ordersAndShipping-eoriDetails";
import { EstimatedShipDateIA } from "../instantAnswers/ordersAndShipping-estimatedShipDate";
import { ExtendReturnIA } from "../instantAnswers/ordersAndShipping-extendReturn";
import { HowDutiesWorkIA } from "../instantAnswers/ordersAndShipping-howDutiesWork";
import { ItemDelayedIA } from "../instantAnswers/ordersAndShipping-itemDelayed";
import { PoBoxIA } from "../instantAnswers/ordersAndShipping-poBox";
import { ReportDamagedItemIA } from "../instantAnswers/ordersAndShipping-reportDamagedItem";
import { ReportMissingItemIA } from "../instantAnswers/ordersAndShipping-reportMissingItem";
import { ReportWrongItemIA } from "../instantAnswers/ordersAndShipping-reportWrongItem";
import { RequestDutiesReimbursementIA } from "../instantAnswers/ordersAndShipping-requestDutiesReimbursement";
import { ShippingCostEstimatesIA } from "../instantAnswers/ordersAndShipping-shippingCostEstimates";
import { UpdateAccountShippingAddressIA } from "../instantAnswers/ordersAndShipping-updateAccountShippingAddress";
import { WhereIsMyOrderIA } from "../instantAnswers/ordersAndShipping-whereIsMyOrder";
import { GeneralContactBrandIA } from "../instantAnswers/shared-generalContactBrand";

export const useOrdersAndShippingRetailerNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    OrdersAndShipping: {
      id: "OrdersAndShipping",
      label: strictLocalize({
        defaultMessage: "Orders & shipping",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <ShippingIcon fontSize="24px" color="faireBlack" />,
      children: [
        "TrackShipment",
        "DamagedMissingOrWrong",
        "Returns",
        "OrdersAndShippingDuties",
        "ShippingAddresses",
        "ImportingProducts",
        "ShippingCosts",
        "OtherOrdersAndShipping",
      ],
      level: 1,
      sidebarTitle: strictLocalize({
        defaultMessage: "Order or shipping issue",
        description: {
          text: "Title for displaying on the order summary sidebar",
        },
      }),
    },
    TrackShipment: {
      id: "TrackShipment",
      label: strictLocalize({
        defaultMessage: "Track shipment",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "WhereIsMyOrder",
        "EstimatedShipDate",
        "ItemDelayed",
        "OtherTrackShipment",
      ],
      level: 2,
    },
    DamagedMissingOrWrong: {
      id: "DamagedMissingOrWrong",
      label: strictLocalize({
        defaultMessage: "Damaged, missing or wrong items or shipments",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "ReportDamagedItem",
        "ReportMissingItem",
        "WrongItemsSent",
        "OtherDamagedMissingOrWrong",
      ],
      level: 2,
    },
    Returns: {
      id: "Returns",
      label: strictLocalize({
        defaultMessage: "Returns",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "CreateReturn",
        "ExtendReturn",
        "ItemReturnedToBrand",
        "OtherReturns",
      ],
      level: 2,
    },
    OrdersAndShippingDuties: {
      id: "OrdersAndShippingDuties",
      label: strictLocalize({
        defaultMessage: "Duties",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "HowDutiesWork",
        "OrdersAndShippingDutiesInvoice",
        "RequestDutiesReimbursement",
        "OtherOrdersAndShippingDuties",
      ],
      level: 2,
    },
    ShippingAddresses: {
      id: "ShippingAddresses",
      label: strictLocalize({
        defaultMessage: "Shipping addresses",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: ["ChangeShippingAddress", "UpdateShippingAddress", "POBox"],
      level: 2,
    },
    ImportingProducts: {
      id: "ImportingProducts",
      label: strictLocalize({
        defaultMessage: "Importing products",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "GuideToImportingProducts",
        "EORIDetails",
        "ImportedItemReturnedToBrand",
        "OtherImportingProducts",
      ],
      level: 2,
    },
    ShippingCosts: {
      id: "ShippingCosts",
      label: strictLocalize({
        defaultMessage: "Shipping costs",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "ShippingCostEstimates",
        "ShippingCostsWithInsider",
        "OtherShippingCosts",
      ],
      level: 2,
    },
    OtherOrdersAndShipping: {
      id: "OtherOrdersAndShipping",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [
        articles.FulfillingOrders.SHIPPING_WORK,
        articles.FulfillingOrders.SHIPPING_ADDRESS,
      ],
      level: 2,
    },
    WhereIsMyOrder: {
      id: "WhereIsMyOrder",
      label: strictLocalize({
        defaultMessage: "Where is my order",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.ORDER_STATUS],
      instantAnswer: WhereIsMyOrderIA(strictLocalize),
      level: 3,
    },
    EstimatedShipDate: {
      id: "EstimatedShipDate",
      label: strictLocalize({
        defaultMessage: "Estimated ship date",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.ORDER_STATUS],
      instantAnswer: EstimatedShipDateIA(strictLocalize),
      level: 3,
    },
    ItemDelayed: {
      id: "ItemDelayed",
      label: strictLocalize({
        defaultMessage: "Item delayed",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.ORDER_STATUS],
      instantAnswer: ItemDelayedIA(strictLocalize),
      level: 3,
    },

    OtherTrackShipment: {
      id: "OtherTrackShipment",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [
        articles.FulfillingOrders.SHIPPING_WORK,
        articles.FulfillingOrders.SHIPPING_ADDRESS,
      ],
      instantAnswer: GeneralContactBrandIA(strictLocalize),
      level: 3,
    },
    ReportDamagedItem: {
      id: "ReportDamagedItem",
      label: strictLocalize({
        defaultMessage: "Report a damaged item or shipment",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.REPORT_DAMAGED_MISSING],
      instantAnswer: ReportDamagedItemIA(strictLocalize),
      level: 3,
    },
    ReportMissingItem: {
      id: "ReportMissingItem",
      label: strictLocalize({
        defaultMessage: "Report a missing item or shipment",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.REPORT_DAMAGED_MISSING],
      instantAnswer: ReportMissingItemIA(strictLocalize),
      level: 3,
    },
    WrongItemsSent: {
      id: "WrongItemsSent",
      label: strictLocalize({
        defaultMessage: "Wrong items sent",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.MyOrders.REPORT_DAMAGED_MISSING],
      instantAnswer: ReportWrongItemIA(strictLocalize),
      level: 3,
    },

    OtherDamagedMissingOrWrong: {
      id: "OtherDamagedMissingOrWrong",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [articles.MyOrders.REPORT_DAMAGED_MISSING],
      instantAnswer: GeneralContactBrandIA(strictLocalize),
      level: 3,
    },
    CreateReturn: {
      id: "CreateReturn",
      label: strictLocalize({
        defaultMessage: "Create a return",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.ReturnsAndRefunds.MAKE_A_RETURN,
        articles.ReturnsAndRefunds.CANT_RETURN,
      ],
      instantAnswer: CreateReturnIA(strictLocalize),
      level: 3,
    },
    ExtendReturn: {
      id: "ExtendReturn",
      label: strictLocalize({
        defaultMessage: "Extend return window",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.ReturnsAndRefunds.EXTEND_A_RETURN],
      instantAnswer: ExtendReturnIA(strictLocalize),
      level: 3,
    },

    ItemReturnedToBrand: {
      id: "ItemReturnedToBrand",
      label: strictLocalize({
        defaultMessage: "Item returned to brand by carrier",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.ReturnsAndRefunds.MAKE_A_RETURN],
      instantAnswer: GeneralContactBrandIA(strictLocalize),
      level: 3,
    },

    OtherReturns: {
      id: "OtherReturns",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [articles.ReturnsAndRefunds.MAKE_A_RETURN],
      instantAnswer: GeneralContactBrandIA(strictLocalize),
      level: 3,
    },
    HowDutiesWork: {
      id: "HowDutiesWork",
      label: strictLocalize({
        defaultMessage: "How duties work",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.ImportFeesAndDuties.DUTIES_REFUND],
      instantAnswer: HowDutiesWorkIA(strictLocalize, articles),
      level: 3,
    },
    OrdersAndShippingDutiesInvoice: {
      id: "OrdersAndShippingDutiesInvoice",
      label: strictLocalize({
        defaultMessage: "Why did I receive a duties invoice?",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.ImportFeesAndDuties.DUTIES_REFUND?.atSection("0"),
        articles.FAQ.FAIRE_INSIDER_EU?.atSection("6"),
      ],
      instantAnswer: DutiesInvoiceIA(strictLocalize),
      level: 3,
    },
    RequestDutiesReimbursement: {
      id: "RequestDutiesReimbursement",
      label: strictLocalize({
        defaultMessage: "Request duties reimbursement",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.ImportFeesAndDuties.DUTIES_REFUND],
      instantAnswer: RequestDutiesReimbursementIA(strictLocalize, articles),
      level: 3,
    },
    OtherOrdersAndShippingDuties: {
      id: "OtherOrdersAndShippingDuties",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [articles.ImportFeesAndDuties.DUTIES_REFUND],
      level: 3,
    },
    ChangeShippingAddress: {
      id: "ChangeShippingAddress",
      label: strictLocalize({
        defaultMessage: "Change shipping address for an order",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FulfillingOrders.CHANGE_SHIPPING_ADDRESS],
      instantAnswer: ChangeShippingAddressIA(strictLocalize),
      level: 3,
    },
    UpdateShippingAddress: {
      id: "UpdateShippingAddress",
      label: strictLocalize({
        defaultMessage: "Update shipping address on the account",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FulfillingOrders.SHIPPING_ADDRESS],
      instantAnswer: UpdateAccountShippingAddressIA(strictLocalize),
      level: 3,
    },
    POBox: {
      id: "POBox",
      label: strictLocalize({
        defaultMessage: "PO box addresses",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FulfillingOrders.SHIPPING_ADDRESS],
      instantAnswer: PoBoxIA(strictLocalize),
      level: 3,
    },

    GuideToImportingProducts: {
      id: "GuideToImportingProducts",
      label: strictLocalize({
        defaultMessage: "Guide to importing products",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.ImportFeesAndDuties.USA,
        articles.ImportFeesAndDuties.Canada,
        articles.ImportFeesAndDuties.Europe,
        articles.ImportFeesAndDuties.Australia,
      ],
      instantAnswer: GeneralContactBrandIA(strictLocalize),

      level: 3,
    },
    EORIDetails: {
      id: "EORIDetails",
      label: strictLocalize({
        defaultMessage: "EORI details",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.ImportFeesAndDuties.EORI],
      instantAnswer: EoriDetailsIA(strictLocalize),
      level: 3,
    },

    ImportedItemReturnedToBrand: {
      id: "ImportedItemReturnedToBrand",
      label: strictLocalize({
        defaultMessage: "Item returned to brand by carrier",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.ImportFeesAndDuties.USA,
        articles.ImportFeesAndDuties.Canada,
        articles.ImportFeesAndDuties.Europe,
        articles.ImportFeesAndDuties.Australia,
      ],
      instantAnswer: GeneralContactBrandIA(strictLocalize),
      level: 3,
    },
    OtherImportingProducts: {
      id: "OtherImportingProducts",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [
        articles.ImportFeesAndDuties.USA,
        articles.ImportFeesAndDuties.Canada,
        articles.ImportFeesAndDuties.Europe,
        articles.ImportFeesAndDuties.Australia,
      ],
      level: 3,
    },
    ShippingCostEstimates: {
      id: "ShippingCostEstimates",
      label: strictLocalize({
        defaultMessage: "Understanding shipping cost estimates",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FulfillingOrders.SHIPPING_WORK],
      instantAnswer: ShippingCostEstimatesIA(strictLocalize),
      level: 3,
    },
    ShippingCostsWithInsider: {
      id: "ShippingCostsWithInsider",
      label: strictLocalize({
        defaultMessage: "Shipping costs with insider",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.FAQ.FAIRE_INSIDER_EU, articles.FAQ.FAIRE_INSIDER_US],
      level: 3,
    },
    OtherShippingCosts: {
      id: "OtherShippingCosts",
      label: OTHER_NODES_LABEL(strictLocalize),
      articles: [articles.FulfillingOrders.SHIPPING_WORK],
      level: 3,
    },
  };
};
